"use client";

import {
  BadgeCheck,
  BellRing,
  CalendarCheck,
  MessageSquare,
  PhoneMissed,
  UserCheck,
} from "lucide-react";
import { Conversation, type Contact, type ThreadItem } from "../_shared/conversation";
import {
  FeatureUseCases,
  type SceneProps,
  type UseCase,
  useRevealCount,
} from "../_shared/use-cases-section";

/* ------------------------------------------------------------------ */
/* 1 · Every channel, one thread                                      */
/* ------------------------------------------------------------------ */

const PRIYA: Contact = {
  name: "Priya Nair",
  src: "/images/website/customers/priya-nair.jpg",
  status: "New lead",
};

const OMNICHANNEL: ThreadItem[] = [
  {
    kind: "message",
    id: "p1",
    direction: "incoming",
    body: "Hi! Do you have any beginner classes this week?",
  },
  { kind: "event", id: "p2", icon: PhoneMissed, title: "Missed call", sub: "Tue · 4:12 PM" },
  {
    kind: "voicemail",
    id: "p3",
    duration: "0:14",
    transcript:
      "Hey, it's Priya. Just tried calling about beginner classes, hoping to start this week!",
  },
  {
    kind: "message",
    id: "p4",
    direction: "outgoing",
    body: "Hi Priya! We have a beginner Reformer class Thursday at 6pm. Want me to save you a spot?",
  },
  {
    kind: "email",
    id: "p5",
    direction: "incoming",
    subject: "Re: Class schedule",
    body: "That works. Can I pay when I arrive?",
  },
  {
    kind: "message",
    id: "p6",
    direction: "outgoing",
    body: "All set, you're booked for Thursday 6pm. You can pay at the studio.",
  },
  {
    kind: "event",
    id: "p7",
    icon: CalendarCheck,
    title: "Booked first class",
    sub: "Thu · 6:00 PM",
  },
];

function OmnichannelScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, OMNICHANNEL.length, reduce, active);
  return (
    <Conversation
      contact={PRIYA}
      items={OMNICHANNEL}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Context, not guesswork                                         */
/* ------------------------------------------------------------------ */

const SCOTT: Contact = {
  name: "Scott Tinari",
  src: "/images/website/customers/scott-tinari.jpg",
  status: "Subscribed",
  statusTone: "brand",
};

function ContextChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
      {children}
    </span>
  );
}

const CONTEXT_BAND = (
  <div className="flex flex-wrap gap-1.5">
    <ContextChip>615 classes</ContextChip>
    <ContextChip>Member · 3 yrs</ContextChip>
    <ContextChip>Last visit 2 days ago</ContextChip>
  </div>
);

const CONTEXT: ThreadItem[] = [
  {
    kind: "message",
    id: "s1",
    direction: "incoming",
    body: "Any chance you have a spot in tomorrow's 9am?",
  },
  {
    kind: "message",
    id: "s2",
    direction: "outgoing",
    body: "Hey Scott! Saved you a spot in your usual Sat 9am with Mia. That's class 616 🎉",
  },
  {
    kind: "message",
    id: "s3",
    direction: "incoming",
    body: "Amazing, thank you. You always know!",
  },
  { kind: "event", id: "s4", icon: CalendarCheck, title: "Booked", sub: "Sat · 9:00 AM HIIT" },
  {
    kind: "message",
    id: "s5",
    direction: "outgoing",
    body: "And your membership renews next week, you're all set, nothing to do.",
  },
];

function ContextScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, CONTEXT.length, reduce, active);
  return (
    <Conversation
      contact={SCOTT}
      items={CONTEXT}
      visible={visible}
      reduce={reduce}
      context={CONTEXT_BAND}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Speed to response                                              */
/* ------------------------------------------------------------------ */

const MAYA: Contact = {
  name: "Maya Rivera",
  src: "/images/website/customers/maya-rivera.jpg",
  status: "New lead",
};

const SPEED: ThreadItem[] = [
  { kind: "event", id: "m1", icon: BellRing, title: "New lead · whole team alerted", sub: "now" },
  {
    kind: "message",
    id: "m2",
    direction: "incoming",
    body: "Hi, saw your ad. Do you offer a free trial?",
  },
  {
    kind: "event",
    id: "m3",
    icon: UserCheck,
    title: "Jordan replied first",
    sub: "Assigned to Jordan",
  },
  {
    kind: "message",
    id: "m4",
    direction: "outgoing",
    body: "Hi Maya! Yes, your first class is on us. Want me to book you in this week?",
  },
  { kind: "message", id: "m5", direction: "incoming", body: "Yes please, Saturday morning?" },
  {
    kind: "event",
    id: "m6",
    icon: MessageSquare,
    title: "Reassigned to Mia with a note",
    sub: "Mia notified",
  },
  {
    kind: "message",
    id: "m7",
    direction: "outgoing",
    body: "Mia here, your coach for Saturday! Booked you for 9am, can't wait to meet you.",
  },
  { kind: "event", id: "m8", icon: BadgeCheck, title: "First class booked", sub: "Sat · 9:00 AM" },
];

function SpeedScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, SPEED.length, reduce, active);
  return (
    <Conversation contact={MAYA} items={SPEED} visible={visible} reduce={reduce} flow={flow} />
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

const CASES: UseCase[] = [
  {
    id: "omnichannel",
    title: "From first text to first class",
    description:
      "A lead texts, misses a call, leaves a voicemail, then emails. It all stays in one thread, so the booking never slips between apps.",
    Scene: OmnichannelScene,
    steps: OMNICHANNEL.length,
  },
  {
    id: "context",
    title: "Know them before you reply",
    description:
      "Membership, spend, class history, and upcoming bookings sit right beside the chat, so the reply is personal even from someone covering the desk for the first time.",
    Scene: ContextScene,
    steps: CONTEXT.length,
  },
  {
    id: "speed",
    title: "First to reply wins the lead",
    description:
      "The moment a lead comes in, the whole team gets pinged. The first to answer becomes the owner, then passes it to the right coach with a quick note when the timing's right.",
    Scene: SpeedScene,
    steps: SPEED.length,
  },
];

export function UnifiedInboxUseCases() {
  return (
    <FeatureUseCases
      title={
        <>
          One inbox. Every conversation.
          <span className="text-muted-foreground">
            {" "}
            See how studios turn scattered messages into booked classes, faster and more personal.
          </span>
        </>
      }
      cases={CASES}
    />
  );
}
